import { Button, buttonVariants } from "@beringlab/ui"
import * as Sentry from "@sentry/react"
import { Link, useRouter, type ErrorComponentProps } from "@tanstack/react-router"
import { useEffect } from "react"
import { useTranslation } from "react-i18next"

/**
 * What a page shows when its loader or component throws: the router's default error component.
 * It replaces per-route `{error.message} error in … page.` divs, which put developer text in
 * front of users and offered no way forward.
 */
const RouteErrorPage = ({ error, reset }: ErrorComponentProps) => {
  const { t } = useTranslation()
  const router = useRouter()

  useEffect(() => {
    // The route's error boundary catches this before Sentry's global handler could see it.
    Sentry.captureException(error, { tags: { boundary: "route" } })
  }, [error])

  return (
    <main className="grid min-h-full place-items-center bg-card px-6 py-24 sm:py-32 lg:px-8">
      <div className="text-center">
        <h1 className="text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl">
          {t("common.errorPageTitle")}
        </h1>
        <p className="mt-4 text-base leading-7 text-gray-600">
          {t("common.errorPageDescription")}
        </p>
        <div className="mt-10 flex items-center justify-center gap-x-3">
          <Button
            onClick={() => {
              // reset clears a render error; invalidate re-runs the loaders that may have thrown.
              reset()
              void router.invalidate()
            }}
          >
            {t("common.retry")}
          </Button>
          <Link to="/" className={buttonVariants({ variant: "outline" })}>
            {t("common.goHome")}
          </Link>
        </div>
      </div>
    </main>
  )
}

export default RouteErrorPage
